import React, { useState, useEffect, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { addStyles, EditableMathField, StaticMathField } from "react-mathquill";
import {
  Dialog,
  DialogActions,
  DialogContent, Card, CardContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Box,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import TagModal from "./components/TagModal";
import KaTeXTextField from "./KatexTextField";
import StyleIcon from "@mui/icons-material/Style";
import katex from "katex";
import "katex/dist/katex.min.css";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import sanitizeHtml from "sanitize-html";
import { useContext } from "react";
import { ThemeContext } from "./context/ThemeContext";
import "react-quill/dist/quill.snow.css";
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from '@mui/material/styles';
import skip from "./components/assets/skip.png";
import forgot from "./components/assets/forgot.png";
import partially from "./components/assets/partially.png";
import efforts from "./components/assets/efforts.png";
import convenient from "./components/assets/convenient.png";

const ImageButton = styled(IconButton)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  transition: "transform 0.2s",
  "&:hover": {
    transform: "scale(1.1)",
  },
}));

const Image = styled("img")(() => ({
  width: "50px",
  height: "50px",
  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
}));

// Import the Clipboard module from Quill
const Clipboard = Quill.import("modules/clipboard");

// Enable KaTeX formula button in ReactQuill
const Formula = Quill.import('formats/formula');
if (Formula) {
  window.katex = katex; // Make katex globally available for Quill
}

const RichInlineEditor = ({ value, onChange, placeholder = "" }) => {
  const modules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ script: "sub" }, { script: "super" }],
      ["formula"],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = ["bold", "italic", "underline", "script", "formula"];


  return (
    <ReactQuill
      value={value || ""}
      onChange={onChange}
      modules={modules}
      formats={formats}
      placeholder={placeholder}
      style={{
        marginBottom: "12px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        minHeight: "60px", // ADD THIS
        maxHeight: "120px", // ADD THIS
      }}
      theme="snow"
    />
  );
};

class CustomClipboard extends Clipboard {
  onPaste(e) {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const html = clipboardData.getData("text/html");

    if (html) {
      const sanitizedHtml = sanitizeHtml(html, {
        allowedTags: sanitizeHtml.defaults.allowedTags.concat([
          "b",
          "i",
          "em",
          "strong",
          "span",
          "br",
          "u",
          "sub",
          "sup",
          "a",
          "p",
          "img",
        ]),
        allowedAttributes: false, // Strip all attributes except essential ones
      });

      // Insert the HTML at the cursor position
      const range = this.quill.getSelection();
      if (range) {
        this.quill.clipboard.dangerouslyPasteHTML(range.index, sanitizedHtml);
      }
    } else {
      const text = clipboardData.getData("text/plain");
      // Insert plain text as HTML with line breaks
      const formattedText = `<p>${text.replace(/\n/g, "<br>")}</p>`;
      const range = this.quill.getSelection();
      if (range) {
        this.quill.clipboard.dangerouslyPasteHTML(range.index, formattedText);
      }
    }
  }
}

// Register the custom clipboard
Quill.register("modules/clipboard", CustomClipboard);

addStyles();

const sanitizeRichText = (html = "") => {
  if (!html) return "";

  return sanitizeHtml(html, {
    allowedTags: [
      "p",
      "br",
      "strong",
      "em",
      "b",
      "i",
      "u",
      "s",
      "sub",
      "sup",
      "ol",
      "ul",
      "li",
      "span",
      "div",
    ],
    allowedAttributes: {
      span: ["class", "data-value", "style"],
      div: ["style"],
      p: ["style"],
    },
    allowedClasses: {
      span: ["ql-formula"],
    },
    allowedStyles: {
      "*": {
        color: [/.*/],
        "background-color": [/.*/],
        "font-size": [/.*/],
        "text-align": [/^left$/, /^right$/, /^center$/, /^justify$/],
      },
    },
    transformTags: {
      h1: "p",
      h2: "p",
      h3: "p",
      h4: "p",
      h5: "p",
      h6: "p",
    },
  });
};

function FillInTheBlanks() {
  const [isLoading, setIsLoading] = useState(true);
  const [folderType, setFolderType] = useState(null); // 'own', 'free', or 'paid'
  const [isAssignedFolder, setIsAssignedFolder] = useState(false);
  const [cards, setCards] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [questionsList, setQuestionsList] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null);
  const [currentAnswers, setCurrentAnswers] = useState([]);
  const [currentOptions, setCurrentOptions] = useState([]); // New state for options
  const [currentExplanation, setCurrentExplanation] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCardId, setDeleteCardId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCard, setEditCard] = useState(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editAnswers, setEditAnswers] = useState("");
  const [editExplanation, setEditExplanation] = useState("");
  const { subfolder_id } = useParams();
  const [selectedText, setSelectedText] = useState("");
  const [isExplanation, setIsExplanation] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [dropdownVisible, setDropdownVisible] = useState(false); // Controls dropdown visibility
  const [selectedOption, setSelectedOption] = useState(""); // State to track selected option
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagCardIndex, setTagCardIndex] = useState(null);
  const [tagModalPosition, setTagModalPosition] = useState({ top: 0, left: 0 });

  const [userGuideOpen, setUserGuideOpen] = useState(false);

  // Tags states for new question creation
  const [tags, setTags] = useState([]);
  const [tagsInput, setTagsInput] = useState('');


  const [editorOpen, setEditorOpen] = useState(false);
  const [editorPosition, setEditorPosition] = useState({ top: 0, left: 0 });
  const [currentTextField, setCurrentTextField] = useState(null);
  // New states for editing options
  const [editOptions, setEditOptions] = useState([]); // option text
  const [editIsCorrect, setEditIsCorrect] = useState([]); // boolean flags
  const [editOptionExplanations, setEditOptionExplanations] = useState([]); // explanation per option

  // Popover Editor States
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [popoverContent, setPopoverContent] = useState("");
  const [editingField, setEditingField] = useState(null); // 'question' or 'explanation'
  const [selectionRange, setSelectionRange] = useState(null);

  // States for viewing flashcard details
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewedCard, setViewedCard] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedFeeling, setSelectedFeeling] = useState(null);

  const questionRef = useRef(null);
  const explanationRef = useRef(null);
  const navigate = useNavigate();
  // const { darkMode } = useContext(ThemeContext);
  const { mode, customColors } = useContext(ThemeContext);
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Add to state declarations
  const [permissions, setPermissions] = useState({
    can_create_fib: true,
    can_read_fib: true,
    can_update_fib: true,
    can_delete_fib: true,
    can_start_flashcard: true,
    can_start_quiz: true,
    is_admin: false,
  });
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const initializeComponent = async () => {
      setIsLoading(true);

      try {
        // Step 1: Check folder type
        const token = sessionStorage.getItem("authToken");
        const folderResponse = await axios.get(`${apiUrl}/folders/`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const isOwn = folderResponse.data.own_folders.some(folder =>
          folder.id === parseInt(subfolder_id) ||
          folder.subfolders?.some(sf => sf.id === parseInt(subfolder_id))
        );

        if (isOwn) {
          setFolderType('own');
          setIsAssignedFolder(false);
        } else {
          const isFree = folderResponse.data.assigned_folders.free.some(folder =>
            folder.id === parseInt(subfolder_id) ||
            folder.subfolders?.some(sf => sf.id === parseInt(subfolder_id))
          );

          if (isFree) {
            setFolderType('free');
            setIsAssignedFolder(true);
          } else {
            setFolderType('paid');
            setIsAssignedFolder(true);
          }
        }

        // Step 2: Fetch permissions
        const userRole = sessionStorage.getItem("userRole");
        let permissionsData;

        if (userRole === "admin") {
          permissionsData = {
            can_create_fib: true,
            can_read_fib: true,
            can_update_fib: true,
            can_delete_fib: true,
            can_start_flashcard: true,
            can_start_quiz: true,
            is_admin: true,
          };
          setPermissions(permissionsData);
        } else {
          const permResponse = await axios.get(`${apiUrl}/user/permissions/`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          const {
            can_create_fib,
            can_read_fib,
            can_update_fib,
            can_delete_fib,
            can_start_flashcard,
            can_start_quiz,
          } = permResponse.data.permissions || {};

          permissionsData = {
            can_create_fib: can_create_fib || false,
            can_read_fib: can_read_fib || false,
            can_update_fib: can_update_fib || false,
            can_delete_fib: can_delete_fib || false,
            can_start_flashcard: can_start_flashcard || false,
            can_start_quiz: can_start_quiz || false,
            is_admin: false,
          };
          setPermissions(permissionsData);
        }

        // Step 3: Fetch flashcards if user has permission
        if (permissionsData.can_read_fib || permissionsData.is_admin) {
          const flashcardsResponse = await axios.get(
            `${apiUrl}/subfolder/${subfolder_id}/fib/`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          const fetchedCards = flashcardsResponse.data.map((item) => ({
            id: item.id,
            question: item.statement,
            question_type: item.question_type,
            answer: item.answers,
            explanation: item.explanation,
            tags: item.tags,
            expanded: false,
          }));
          setCards(fetchedCards);
        }

      } catch (error) {
        console.error("Failed to initialize component:", error);
        if (error.response?.status === 401) {
          showSnackbar("You are not authenticated. Please login again.", "error");
        } else {
          showSnackbar("Failed to load data. Please try again.", "error");
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeComponent();
  }, [subfolder_id, apiUrl]);

  const fetchFlashcardsFromServer = async () => {
    // Keep this function as-is for when you need to refresh cards
    // Just remove the setIsLoading calls from it
    if (!permissions.can_read_fib && !permissions.is_admin) {
      showSnackbar("You don't have permission to view FIB Questions.", "warning");
      return;
    }

    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        console.error("Access token is missing. Please log in again.");
        showSnackbar("You are not authenticated. Please login again.", "error");
        return;
      }
      const response = await axios.get(
        `${apiUrl}/subfolder/${subfolder_id}/fib/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const fetchedCards = response.data.map((item) => ({
        id: item.id,
        question: item.statement,
        question_type: item.question_type,
        answer: item.answers,
        explanation: item.explanation,
        tags: item.tags,
        expanded: false,
      }));
      setCards(fetchedCards);
    } catch (error) {
      console.error("Failed to fetch flashcards from server", error);
      showSnackbar("Failed to fetch flashcards. Please try again.", "error");
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
  };

  const handleTextSelect = () => {
    const textArea = document.getElementById("questionTextField");
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const selected = textArea.value.substring(start, end);
    setSelectedText(selected);
  };

  const handleExplanationSelect = () => {
    const textArea = document.getElementById("explanationTextField");
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const selected = textArea.value.substring(start, end);
    setSelectedText(selected);
  };

  const toggleCardExpand = (index) => {
    const newCards = [...cards];
    newCards[index].expanded = !newCards[index].expanded;
    setCards(newCards);
  };

  // Update openTagModal
  const openTagModal = (index, event) => {
    const iconRect = event.target.getBoundingClientRect();
    setTagCardIndex(index);
    setTagModalPosition({
      top: iconRect.bottom + window.scrollY,
      left: iconRect.left + window.scrollX,
    });
    setTagModalOpen(true);
  };

  const openDeleteDialog = (id) => {
    if (!permissions.can_delete_fib && !permissions.is_admin) {
      showSnackbar(
        "You don't have permission to delete FIB Questions.",
        "warning"
      );
      return;
    }
    setDeleteCardId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCard = async () => {
    if (!permissions.can_delete_fib && !permissions.is_admin) {
      showSnackbar(
        "You don't have permission to delete FIB Questions.",
        "warning"
      );
      return;
    }
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        showSnackbar("You are not authenticated. Please login again.", "error");
        return;
      }
      const response = await axios.delete(
        `${apiUrl}/subfolder/${subfolder_id}/fib/${deleteCardId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const updatedCards = cards.filter((card) => card.id !== deleteCardId);
      setCards(updatedCards);
      setDeleteDialogOpen(false);
      showSnackbar("Flashcard deleted successfully!", "success");
    } catch (error) {
      console.error("Failed to delete flashcard from server", error);
      showSnackbar("Failed to delete flashcard, please try again.", "error");
    }
  };

  const handleInputChange = (blankIndex, value) => {
    const newAnswers = [...currentAnswers];
    newAnswers[blankIndex] = value;
    setCurrentAnswers(newAnswers);
  };

  // New function to handle option changes
  const handleOptionChange = (blankIndex, optionIndex, field, value) => {
    const newOptions = [...currentOptions];
    if (!newOptions[blankIndex]) {
      newOptions[blankIndex] = [];
    }
    if (!newOptions[blankIndex][optionIndex]) {
      newOptions[blankIndex][optionIndex] = {
        answer: "",
        explanation: "",
        is_correct: false,
      };
    }
    newOptions[blankIndex][optionIndex][field] = value;
    setCurrentOptions(newOptions);
  };

  // New function to add an option for a specific blank
  const addOption = (blankIndex) => {
    const newOptions = [...currentOptions];
    if (!newOptions[blankIndex]) {
      newOptions[blankIndex] = [];
    }
    newOptions[blankIndex].push({
      answer: "",
      explanation: "",
      is_correct: false,
    });
    setCurrentOptions(newOptions);
  };

  const openEditDialog = (card) => {
    if (!permissions.can_update_fib && !permissions.is_admin) {
      showSnackbar(
        "You don't have permission to update FIB Questions.",
        "warning"
      );
      return;
    }

    setEditCard(card);
    setEditQuestion(card.question);
    setEditExplanation(card.explanation || "");

    // Populate options
    setEditOptions(card.answer.map((opt) => opt.answer || ""));
    setEditIsCorrect(card.answer.map((opt) => opt.is_correct || false));
    setEditOptionExplanations(card.answer.map((opt) => opt.explanation || ""));

    setEditDialogOpen(true);
  };

  // New function to open view modal
  const openViewModal = (card) => {
    setViewedCard(card);
    setShowAnswer(false);
    setSelectedFeeling(null);
    setViewModalOpen(true);
  };

  // Update option text
  const handleEditOptionChange = (index, value) => {
    const newOptions = [...editOptions];
    newOptions[index] = value;
    setEditOptions(newOptions);
  };

  // Update is_correct checkbox
  const handleEditCorrectChange = (index, checked) => {
    const newCorrect = [...editIsCorrect];
    newCorrect[index] = checked;
    setEditIsCorrect(newCorrect);
  };

  // Update explanation for option
  const handleEditExplanationChange = (index, value) => {
    const newExplanations = [...editOptionExplanations];
    newExplanations[index] = value;
    setEditOptionExplanations(newExplanations);
  };

  // Remove an option
  const handleEditRemoveOption = (index) => {
    setEditOptions(editOptions.filter((_, i) => i !== index));
    setEditIsCorrect(editIsCorrect.filter((_, i) => i !== index));
    setEditOptionExplanations(
      editOptionExplanations.filter((_, i) => i !== index)
    );
  };

  // Add new blank option
  const addEditOption = () => {
    setEditOptions([...editOptions, ""]);
    setEditIsCorrect([...editIsCorrect, false]);
    setEditOptionExplanations([...editOptionExplanations, ""]);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleTagAdded = (cardId, newTag) => {
    if (!cardId || !newTag || !newTag.name) {
      console.error("Invalid tag data:", { cardId, newTag });
      showSnackbar("Invalid tag data. Please try again.", "error");
      return;
    }

    try {
      const updatedCards = cards.map((card) => {
        if (card.id === cardId) {
          return {
            ...card,
            tags: [...card.tags, { name: newTag.name, id: newTag.id || null }],
          };
        }
        return card;
      });
      setCards(updatedCards);
      setTagModalOpen(false); // Close modal
    } catch (error) {
      console.error("Error updating local state in handleTagAdded:", error);
      showSnackbar("Failed to update tag locally: " + error.message, "error");
    }
  };

  const handleTagUpdated = (cardId, updatedTag) => {
    if (!cardId || !updatedTag || !updatedTag.id || !updatedTag.name) {
      console.error("Invalid tag update data:", { cardId, updatedTag });
      showSnackbar("Invalid tag update data. Please try again.", "error");
      return;
    }

    try {
      const updatedCards = cards.map((card) => {
        if (card.id === cardId) {
          return {
            ...card,
            tags: card.tags.map((tag) =>
              tag.id === updatedTag.id ? { ...tag, name: updatedTag.name } : tag
            ),
          };
        }
        return card;
      });
      setCards(updatedCards);
    } catch (error) {
      console.error("Error updating local state in handleTagUpdated:", error);
      showSnackbar("Failed to update tag locally: " + error.message, "error");
    }
  };

  const handleTagDeleted = (cardId, tagId) => {
    if (!cardId || !tagId) {
      console.error("Invalid tag deletion data:", { cardId, tagId });
      showSnackbar("Invalid tag deletion data. Please try again.", "error");
      return;
    }

    try {
      const updatedCards = cards.map((card) => {
        if (card.id === cardId) {
          return {
            ...card,
            tags: card.tags.filter((tag) => tag.id !== tagId),
          };
        }
        return card;
      });
      setCards(updatedCards);
    } catch (error) {
      console.error("Error updating local state in handleTagDeleted:", error);
      showSnackbar("Failed to delete tag locally: " + error.message, "error");
    }
  };

  const stripHtmlTags = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handleUpdateQuestion = async () => {
    if (!permissions.can_update_fib && !permissions.is_admin) {
      showSnackbar(
        "You don't have permission to update FIB Questions.",
        "warning"
      );
      return;
    }

    const questionHtml = sanitizeRichText(editQuestion);
    const explanationHtml = sanitizeRichText(editExplanation || "");

    const updatedCard = {
      statement: questionHtml,
      question_type: "FIB", // ADD THIS LINE
      answers: editOptions.map((opt, idx) => ({
        answer: opt,
        explanation: editOptionExplanations[idx] || "",
        is_correct: editIsCorrect[idx] || false,
      })),
      explanation: explanationHtml,
      tags: editCard.tags || [], // ADD THIS (safe fallback)
    };

    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        showSnackbar("You are not authenticated. Please login again.", "error");
        return;
      }

      const response = await axios.put(
        `${apiUrl}/subfolder/${subfolder_id}/fib/${editCard.id}/`,
        updatedCard,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Update local state
      const updatedCards = cards.map((card) =>
        card.id === editCard.id
          ? {
            ...card,
            question: questionHtml,
            answer: updatedCard.answers,
            explanation: explanationHtml,
          }
          : card
      );
      setCards(updatedCards);
      setEditDialogOpen(false);
      showSnackbar("Flashcard updated successfully!", "success");
    } catch (error) {
      console.error(
        "Failed to update question on server",
        error.response?.data || error
      );
      showSnackbar(
        error.response?.data?.question_type?.[0] ||
        "Failed to update flashcard.",
        "error"
      );
    }
  };

  const handleEditChange = (setter) => (event) => {
    setter(event.target.value);
  };

  const handleQuestionSubmit = () => {
    const blanksCount = (currentQuestion.match(/\[BLANK\]/g) || []).length;
    setQuestionsList([
      ...questionsList,
      {
        currentQuestion,
        answers: new Array(blanksCount).fill(""),
        submitted: false,
        explanation: "",
      },
    ]);
    setCurrentQuestionIndex(questionsList.length);
    setCurrentAnswers(new Array(blanksCount).fill(""));
    setCurrentOptions(new Array(blanksCount).fill([])); // Initialize options for each blank
  };

  // Updated handleAnswersSubmit to include options in payload
  const handleAnswersSubmit = async () => {
    if (!permissions.can_create_fib && !permissions.is_admin) {
      showSnackbar("You don't have permission to create FIB Questions.", "warning");
      return;
    }

    // Add tags validation - MANDATORY
    if (tags.length === 0) {
      showSnackbar("Please add at least one tag. Tags are mandatory.", "warning");
      return;
    }

    if (
      !currentQuestion.trim() ||
      currentOptions.some(
        (options) =>
          !options ||
          options.length === 0 ||
          options.some((opt) => !opt.answer.trim()) // sirf answer required rakha
      )
    ) {
      showSnackbar(
        "Please fill in the question and at least one option for each blank.",
        "warning"
      );
      return;
    }

    const questionHtml = sanitizeRichText(currentQuestion);
    const questionForComparison = sanitizeRichText(currentQuestion).trim();
    const isDuplicate = cards.some(
      (card) => sanitizeRichText(card.question).trim() === questionForComparison
    );
    if (isDuplicate) {
      setDuplicateDialogOpen(true);
      return;
    }
    const blanksCount = (currentQuestion.match(/\[BLANK\]/g) || []).length;
    const updatedQuestionsList = [...questionsList];
    updatedQuestionsList[currentQuestionIndex] = {
      ...updatedQuestionsList[currentQuestionIndex],
      answers: currentAnswers,
      explanation: currentExplanation,
      submitted: true,
    };
    const explanationHtml = sanitizeRichText(currentExplanation || "");
    const payload = {
      statement: questionHtml,
      created_by: "ScienceStudent567",
      question_type: "FIB",
      answers: currentOptions.flat(),
      explanation: explanationHtml, // ← Now rich HTML
      tags: tags.map(name => ({ name })), // ← Add tags here
    };
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        showSnackbar("You are not authenticated. Please login again.", "error");
        return;
      }
      const response = await axios.post(
        `${apiUrl}/subfolder/${subfolder_id}/fib/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setQuestionsList(updatedQuestionsList);
      setCurrentAnswers([]);
      setCurrentOptions([]); // Reset options
      setCurrentQuestion("");
      setCurrentQuestionIndex(null);
      setCurrentExplanation("");
      setTags([]);           // ← Reset tags
      setTagsInput("");      // ← Reset tags input
      fetchFlashcardsFromServer();
      showSnackbar("Flashcard added successfully!", "success");
    } catch (error) {
      console.error("Error saving question:", error);
      showSnackbar("Failed to save flashcard, please try again.", "error");
    }
  };

  const formatBlanks = (text) => {
    return text.replace(/\[BLANK\]/g, "________");
  };

  const renderContent = (html) => {
    if (!html) return null;

    // Split content into LaTeX and non-LaTeX parts
    const parts = html.split(/(\$\$.+?\$\$|\$.+?\$)/g);

    return (
      <div>
        {parts.map((part, index) => {
          // Handle block-level LaTeX ($$...$$)
          if (part.startsWith("$$") && part.endsWith("$$")) {
            const latex = part.slice(2, -2); // Remove $$ delimiters
            try {
              const renderedLatex = katex.renderToString(latex, {
                displayMode: true,
              });
              return (
                <div
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content block-latex"
                />
              );
            } catch (error) {
              console.error("Block LaTeX rendering error:", error);
              return (
                <div key={index} className="error-content">
                  {part}
                </div>
              );
            }
          }

          // Handle inline LaTeX ($...$)
          if (part.startsWith("$") && part.endsWith("$")) {
            const latex = part.slice(1, -1); // Remove $ delimiters
            try {
              const renderedLatex = katex.renderToString(latex, {
                displayMode: false,
              });
              return (
                <span
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content inline-latex"
                />
              );
            } catch (error) {
              console.error("Inline LaTeX rendering error:", error);
              return (
                <span key={index} className="error-content">
                  {part}
                </span>
              );
            }
          }

          // Handle non-LaTeX content
          try {
            const formattedContent = part.replace(/\n/g, "<br />"); // Replace newlines
            const allowedTags = [
              "b",
              "i",
              "em",
              "strong",
              "span",
              "br",
              "u",
              "sub",
              "sup",
              "a",
              "p",
              "img",
              "h1",
              "h2",
              "h3",
              "h4",
              "h5",
              "h6",
              "ol",
              "ul",
              "li",
              "blockquote",
            ];
            const allowedAttributes = {
              img: ["src", "alt"],
              a: ["href", "target"],
              span: ["style"],
              p: ["style"],
              h1: ["style"],
              h2: ["style"],
              h3: ["style"],
              h4: ["style"],
              h5: ["style"],
              h6: ["style"],
            };

            // Use sanitize-html or DOMPurify with selective sanitization
            const sanitizedContent = sanitizeHtml(formattedContent, {
              allowedTags,
              allowedAttributes,
              transformTags: {
                p: "span", // Convert <p> tags to <span>
                div: "span", // Convert <div> tags to <span>
              },
            });

            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                className="rendered-content inline-text"
              />
            );
          } catch (error) {
            console.error("HTML rendering error:", error);
            return (
              <span key={index} className="error-content">
                {part}
              </span>
            );
          }
        })}
      </div>
    );
  };

  const createBlanks = (question, answers, isCurrent) => {
    const parts = question.split("[BLANK]");

    // Just render the question text with blank placeholders
    return (
      <span style={{ display: "inline-flex", alignItems: "baseline", whiteSpace: "nowrap" }}>
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            <span style={{ display: "inline", whiteSpace: "nowrap" }}>
              {renderContent(part)}
            </span>
            {index < parts.length - 1 && (
              <span style={{ display: "inline-block", margin: "0 5px", fontWeight: "bold", color: "#1e3a8a" }}>
                [BLANK {index + 1}]
              </span>
            )}
          </React.Fragment>
        ))}
      </span>
    );
  };

  const handleQuestionKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleQuestionSubmit();
    }
  };

  const handleQuestionChange = (value) => {
    // Use the ReactQuill value directly
    let input = value;
    const formattedQuestion = input.replace(/_/g, "[BLANK]");
    setCurrentQuestion(formattedQuestion);
  };

  const handleIconClick = () => {
    // Toggle dropdown visibility when icon is clicked
    setDropdownVisible(!dropdownVisible);
  };

  // Handle dropdown option selection
  const handleDropdownChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedOption(selectedValue);

    const hardCodedText = "fib";

    if (
      selectedValue === "practiceCard" &&
      !permissions.can_start_flashcard &&
      !permissions.is_admin
    ) {
      showSnackbar(
        "You don't have permission to start Practice Flashcard.",
        "warning"
      );
      return;
    }
    if (
      selectedValue === "quizPractice" &&
      !permissions.can_start_quiz &&
      !permissions.is_admin
    ) {
      showSnackbar(
        "You don't have permission to start Quiz Practice.",
        "warning"
      );
      return;
    }

    if (selectedValue === "practiceCard") {
      navigate(
        `/practiceparticularcard/${subfolder_id}/${encodeURIComponent(
          hardCodedText
        )}`
      );
    } else if (selectedValue === "spacedrep") {
      navigate(
        `/practicespacedrep/${subfolder_id}/${encodeURIComponent(
          hardCodedText
        )}`
      );
    } else if (selectedValue === "quizPractice") {
      navigate(`/fibquiz/${subfolder_id}/${encodeURIComponent(hardCodedText)}`);
    }

    setDropdownVisible(false);
  };

  //  hanleConfirmDuplicate to construct correct payload
  const handleConfirmDuplicate = async () => {
    if (!permissions.can_create_fib && !permissions.is_admin) {
      showSnackbar("You don't have permission to create FIB Questions.", "warning");
      setDuplicateDialogOpen(false);
      return;
    }

    // Tags validation for duplicate
    if (tags.length === 0) {
      showSnackbar("Please add at least one tag. Tags are mandatory.", "warning");
      setDuplicateDialogOpen(false);
      return;
    }

    const blanksCount = (currentQuestion.match(/\[BLANK\]/g) || []).length;
    const updatedQuestionsList = [...questionsList];
    updatedQuestionsList[currentQuestionIndex] = {
      ...updatedQuestionsList[currentQuestionIndex],
      answers: currentAnswers,
      explanation: currentExplanation,
      submitted: true,
    };
    const questionHtml = sanitizeRichText(currentQuestion);
    const explanationHtml = sanitizeRichText(currentExplanation || "");
    const payload = {
      statement: questionHtml,
      created_by: "ScienceStudent567",
      question_type: "FIB",
      answers: currentOptions.flat(), // Flatten currentOptions to get array of answer objects
      explanation: explanationHtml, // ← Now rich HTML
      tags: tags.map(name => ({ name })), // ← Add tags here
    };
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        showSnackbar("You are not authenticated. Please login again.", "error");
        return;
      }
      const response = await axios.post(
        `${apiUrl}/subfolder/${subfolder_id}/fib/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setQuestionsList(updatedQuestionsList);
      setCurrentAnswers([]);
      setCurrentOptions([]); // Reset options
      setCurrentQuestion("");
      setCurrentQuestionIndex(null);
      setCurrentExplanation("");
      setTags([]);           // ← Reset tags
      setTagsInput("");      // ← Reset tags input
      fetchFlashcardsFromServer();
      setDuplicateDialogOpen(false);
      showSnackbar("Flashcard added successfully!", "success");
    } catch (error) {
      console.error("Error saving question:", error);
      showSnackbar("Failed to save flashcard, please try again.", "error");
    }
  };

  const handleCancelDuplicate = () => {
    setDuplicateDialogOpen(false);
  };

  const styles = {
    requiredAsterisk: {
      color: "red",
      marginLeft: "4px",
      display: "inline-block",
    },
    label: {
      display: "flex",
      alignItems: "center",
      marginBottom: "8px",
    },
    quillContainer: {
      wordBreak: "break-word",
      overflowWrap: "break-word",
      position: "relative",
    },
    tooltipContainer: {
      display: "inline-block", // Ensures tooltip applies to the entire field
      width: "100%", // Matches ReactQuill width
    },
  };

  return (
    <div className="fill-in-the-blanks" style={{ /* existing styles */ }}>
      {/* ─── SKELETON LOADER (replaces old CircularProgress spinner) ─── */}
      {isLoading ? (
        <Box sx={{ p: 2 }}>
          {/* Header skeleton: title + info icon button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Skeleton
              variant="text"
              width="50%"
              height={48}
              sx={{
                borderRadius: "8px",
                bgcolor: mode === "dark" ? "grey.800" : "grey.200",
              }}
            />
            <Skeleton
              variant="circular"
              width={40}
              height={40}
              sx={{ bgcolor: mode === "dark" ? "grey.800" : "grey.200" }}
            />
          </Box>

          {/* Question input card skeleton */}
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: "12px",
              border: mode === "dark" ? "1px solid #333" : "1px solid #e0e7ff",
              backgroundColor: mode === "dark" ? "#1E1E2E" : "#fff",
              boxShadow:
                mode === "dark"
                  ? "0 4px 14px rgba(0,0,0,0.6)"
                  : "0 4px 12px rgba(15,23,42,0.08)",
            }}
          >
            <Skeleton
              variant="text"
              width="20%"
              height={24}
              sx={{
                mb: 1,
                bgcolor: mode === "dark" ? "grey.800" : "grey.200",
              }}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={80}
              sx={{
                borderRadius: "4px",
                bgcolor: mode === "dark" ? "grey.800" : "grey.200",
              }}
            />
          </Box>

          {/* Card list skeletons — 4 placeholder cards */}
          {[...Array(4)].map((_, i) => (
            <Box
              key={i}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: "16px",
                border:
                  mode === "dark" ? "1px solid #333" : "1px solid #e0e7ff",
                backgroundColor: mode === "dark" ? "#2A2A3E" : "#fff",
                boxShadow:
                  mode === "dark"
                    ? "0 4px 14px rgba(0,0,0,0.6)"
                    : "0 4px 12px rgba(15,23,42,0.08)",
              }}
            >
              {/* Question row: Q.N badge + question text */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5,
                  paddingRight: "60px",
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width={45}
                  height={40}
                  sx={{
                    borderRadius: "10px",
                    mr: 2,
                    flexShrink: 0,
                    bgcolor: mode === "dark" ? "grey.800" : "grey.200",
                  }}
                />
                <Skeleton
                  variant="text"
                  width="70%"
                  height={28}
                  sx={{ bgcolor: mode === "dark" ? "grey.800" : "grey.200" }}
                />
              </Box>
              {/* Tag chip skeletons */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Skeleton
                  variant="rounded"
                  width={60}
                  height={22}
                  sx={{
                    borderRadius: "999px",
                    bgcolor: mode === "dark" ? "grey.800" : "grey.200",
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width={80}
                  height={22}
                  sx={{
                    borderRadius: "999px",
                    bgcolor: mode === "dark" ? "grey.800" : "grey.200",
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width={50}
                  height={22}
                  sx={{
                    borderRadius: "999px",
                    bgcolor: mode === "dark" ? "grey.800" : "grey.200",
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        // ─── MAIN CONTENT (unchanged) ───
        <>
          <div
            className="fill-in-the-blanks"
            style={{
              maxWidth: "1200px",
              margin: "8px 16px 24px 12px", // very small top/side margins
              padding: "0 4px 24px 0", // no extra top padding
            }}
          >
            {!isAssignedFolder ? (
              <>
                <div style={{ position: "relative", marginBottom: "12px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      gap: "8px",
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        mb: 3,
                        fontWeight: '800',
                        background: mode === "dark" ? "linear-gradient(135deg, #f0f7ff 0%, #e0e7ff 100%)" : mode === "custom" ? "#f5f5f5" || "#222222" : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: 'text'
                      }}
                    >
                      Fill in the blanks Questions
                    </Typography>


                    <div style={{ position: "relative" }}>

                      <div style={{ position: "relative" }}>
                        {/* <StyleIcon
                onClick={() => setDropdownVisible(!dropdownVisible)}
                sx={{ cursor: "pointer", color: "primary.dark", '&:focus': { outline: 'none' } }}
              /> */}
                        <Tooltip title="User Guide">
                          <IconButton
                            onClick={() => setUserGuideOpen(true)}
                            sx={{
                              color: mode === "dark" ? "#90caf9" : "#1e8bafff",
                              backgroundColor: mode === "dark" ? "rgba(144, 202, 249, 0.1)" : "rgba(30, 139, 175, 0.1)",
                              "&:hover": {
                                backgroundColor: mode === "dark" ? "rgba(144, 202, 249, 0.2)" : "rgba(30, 139, 175, 0.2)",
                              },
                              '&:focus': { outline: 'none' }
                            }}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                        {dropdownVisible && (
                          <div
                            style={{
                              position: "absolute",
                              top: "30px",
                              right: "-30px",
                              zIndex: 1,
                              backgroundColor:
                                mode === "dark"
                                  ? "#1E1E2E"
                                  : mode === "custom"
                                    ? customColors.background
                                    : "#e3f2fd", // light mode
                              color:
                                mode === "dark"
                                  ? "#E0E0E6"
                                  : mode === "custom"
                                    ? customColors.text
                                    : "#000000", // light mode
                              border: "1px solid #ccc",
                              borderRadius: "4px",
                              padding: "0",
                            }}
                          >
                            {/* <div
                    onClick={() =>
                      handleDropdownChange({
                        target: { value: "practiceCard" },
                      })
                    }
                    style={{
                      cursor:
                        permissions.can_start_flashcard || permissions.is_admin
                          ? "pointer"
                          : "not-allowed",
                      padding: "10px 15px",
                      transition: "background-color 0.3s ease",
                      whiteSpace: "nowrap",
                      opacity:
                        permissions.can_start_flashcard || permissions.is_admin
                          ? 1
                          : 0.5,
                    }}
                    onMouseEnter={(e) => {
                      if (
                        permissions.can_start_flashcard ||
                        permissions.is_admin
                      ) {
                        e.currentTarget.style.backgroundColor =
                          mode === "dark"
                            ? "#50565cff"
                            : mode === "custom"
                              ? customColors.background
                              : "#ffffff"; // light mode default
                      }
                    }}
                    onMouseLeave={(e) =>
                      (permissions.can_start_flashcard ||
                        permissions.is_admin) &&
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    Practice FlashCard
                  </div> */}
                            {/* <div
                    onClick={() =>
                      handleDropdownChange({ target: { value: "spacedrep" } })
                    }
                    style={{
                      cursor: "pointer",
                      padding: "10px 15px",
                      transition: "background-color 0.3s ease",
                      whiteSpace: "nowrap",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = darkMode ? "#50565cff" : "#bbdefb")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    Practice with Spaced Repetition
                  </div> */}
                            {/* <div
                    onClick={() =>
                      handleDropdownChange({
                        target: { value: "quizPractice" },
                      })
                    }
                    style={{
                      cursor:
                        permissions.can_start_quiz || permissions.is_admin
                          ? "pointer"
                          : "not-allowed",
                      padding: "10px 15px",
                      transition: "background-color 0.3s ease",
                      whiteSpace: "nowrap",
                      opacity:
                        permissions.can_start_quiz || permissions.is_admin
                          ? 1
                          : 0.5,
                    }}
                    onMouseEnter={(e) => {
                      if (permissions.can_start_quiz || permissions.is_admin) {
                        e.currentTarget.style.backgroundColor =
                          mode === "dark"
                            ? "#50565cff"
                            : mode === "custom"
                              ? customColors.background
                              : "#ffffff"; // light mode default
                      }
                    }}
                    onMouseLeave={(e) =>
                      (permissions.can_start_quiz || permissions.is_admin) &&
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    Quiz Practice
                  </div> */}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <Card sx={{ mb: 2, borderRadius: "12px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)", backgroundColor: mode === "dark" ? "#1E1E2E" : "#fff", border: "1px solid", borderColor: mode === "dark" ? "#333" : "#e0e7ff" }}>
                  <CardContent>
                    <div style={styles.quillContainer}>
                      <label style={styles.label}>
                        Question
                        <span style={styles.requiredAsterisk}>*</span>
                      </label>
                      <Tooltip title="Press Shift + Underscore, then press Enter to insert a blank and enter your answer.">
                        <div style={styles.tooltipContainer}>
                          <ReactQuill
                            id="questionTextField"
                            variant="outlined"
                            value={currentQuestion}
                            onChange={handleQuestionChange}
                            onKeyDown={handleQuestionKeyDown}
                            placeholder="Enter your question here and press '_' to insert a blank. And bring the cursor at the end and press 'Enter' to add options"
                            modules={{
                              toolbar: [
                                ["bold", "italic", "underline", "strike"],
                                ["blockquote", "code-block"],
                                [{ header: 1 }, { header: 2 }],
                                [{ list: "ordered" }, { list: "bullet" }],
                                [{ script: "sub" }, { script: "super" }],
                                [{ indent: "-1" }, { indent: "+1" }],
                                [{ direction: "rtl" }],
                                [{ size: ["small", false, "large", "huge"] }],
                                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                                [{ color: [] }, { background: [] }],
                                [{ font: [] }],
                                [{ align: [] }],
                                ["clean"],
                              ],
                            }}
                            formats={[
                              "header", "font", "size", "bold", "italic", "underline", "strike",
                              "blockquote", "code-block", "list", "bullet", "indent", "link",
                              "image", "video", "color", "background", "align", "script",
                              { name: "preserve-spaces", type: "inline" },
                            ]}
                            style={{ wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "pre-wrap" }}
                          />
                        </div>
                      </Tooltip>
                      <div className="mt-2" style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                        {renderContent(formatBlanks(currentQuestion))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {currentQuestionIndex !== null && (
                  <>
                    <Card sx={{ mb: 2, borderRadius: "12px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)", backgroundColor: mode === "dark" ? "#1E1E2E" : "#fff", border: "1px solid", borderColor: mode === "dark" ? "#333" : "#e0e7ff" }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          Answer Options for Blanks
                        </Typography>

                        {(currentQuestion.match(/\[BLANK\]/g) || []).map((_, blankIndex) => (
                          <div key={blankIndex} style={{ marginBottom: "24px", padding: "16px", borderRadius: "8px", backgroundColor: mode === "dark" ? "#252535" : "#f8fafc", border: "1px solid", borderColor: mode === "dark" ? "#333" : "#e0e7ff" }}>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: mode === "dark" ? "#fff" : "#1e3a8a" }}>
                              Blank {blankIndex + 1}
                            </Typography>

                            {(currentOptions[blankIndex] || []).map((option, optIndex) => (
                              <Card key={optIndex} sx={{ mb: 1.5, borderRadius: "8px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)", backgroundColor: mode === "dark" ? "#1E1E2E" : "#fff", border: "1px solid", borderColor: mode === "dark" ? "#333" : "#e0e7ff" }}>
                                <CardContent sx={{ padding: "12px !important" }}>
                                  <div style={{ padding: "10px", border: "1px solid #ddd", borderRadius: "6px", backgroundColor: mode === "dark" ? "#252535" : "#fafafa" }}>

                                    <div style={{ marginBottom: "12px" }}>
                                      <strong style={{ display: "block", marginBottom: "6px", fontSize: "0.85rem" }}>
                                        Option {optIndex + 1}:
                                      </strong>
                                      <RichInlineEditor
                                        value={option.answer || ""}
                                        onChange={(val) => handleOptionChange(blankIndex, optIndex, "answer", val)}
                                        placeholder="e.g. H₂O, √(x+1), 2² = 4"
                                      />
                                    </div>

                                    <div style={{ marginBottom: "12px" }}>
                                      <strong style={{ display: "block", marginBottom: "6px", fontSize: "0.85rem" }}>
                                        Explanation:
                                      </strong>
                                      <RichInlineEditor
                                        value={option.explanation || ""}
                                        onChange={(val) => handleOptionChange(blankIndex, optIndex, "explanation", val)}
                                        placeholder="Why is this correct/incorrect?"
                                      />
                                    </div>

                                    <label style={{ display: "flex", alignItems: "center", marginTop: "6px", fontSize: "0.9rem", cursor: "pointer" }}>
                                      <input
                                        type="checkbox"
                                        checked={option.is_correct || false}
                                        onChange={(e) => handleOptionChange(blankIndex, optIndex, "is_correct", e.target.checked)}
                                        style={{ marginRight: "6px", cursor: "pointer" }}
                                      />
                                      <span>Mark as Correct Answer</span>
                                    </label>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}

                            <Button
                              variant="outlined"
                              onClick={() => addOption(blankIndex)}
                              sx={{
                                marginTop: "8px",
                                color: mode === "dark" ? "#ffffff" : mode === "custom" ? customColors.text : "#000000",
                                borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.5)" : mode === "custom" ? customColors.text : "rgba(0, 0, 0, 0.3)",
                                backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.08)" : mode === "custom" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.04)",
                                fontSize: "0.875rem",
                                padding: "8px 20px",
                                borderRadius: "8px",
                                textTransform: "none",
                                "&:hover": {
                                  borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.8)" : mode === "custom" ? customColors.text : "rgba(0, 0, 0, 0.5)",
                                  backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.12)" : mode === "custom" ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.08)",
                                },
                              }}
                              startIcon={<AddIcon />}
                            >
                              Add Option for Blank {blankIndex + 1}
                            </Button>
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    {/* Add Tags Card - Separate Card */}
                    <Card sx={{ mb: 2, borderRadius: "12px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)", backgroundColor: mode === "dark" ? "#1E1E2E" : "#fff", border: "1px solid", borderColor: mode === "dark" ? "#333" : "#e0e7ff" }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          Add Tags <span style={{ color: '#ef4444', fontSize: '0.9rem' }}>(Mandatory)</span>
                        </Typography>
                        <div style={styles.quillContainer}>
                          <Tooltip title="Add tags to organize your questions (mandatory)">
                            <div style={styles.tooltipContainer}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                marginBottom: '8px'
                              }}>
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  placeholder="Enter tags (comma-separated, e.g., Science, Biology, Cells)"
                                  value={tagsInput}
                                  onChange={(e) => {
                                    const input = e.target.value;
                                    setTagsInput(input);
                                    const newTags = input.split(',').map(tag => tag.trim()).filter(tag => tag !== '');
                                    setTags(newTags);
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      '& fieldset': {
                                        borderColor: mode === 'dark' ? '#555' : '#e0e7ff',
                                      },
                                    },
                                  }}
                                />
                              </div>

                              {/* Display added tags */}
                              {tags.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                                  {tags.map((tag, index) => (
                                    <Chip
                                      key={index}
                                      label={`#${tag}`}
                                      onDelete={() => {
                                        const newTags = tags.filter((_, i) => i !== index);
                                        setTags(newTags);
                                        setTagsInput(newTags.join(', '));
                                      }}
                                      sx={{
                                        backgroundColor: mode === 'dark' ? '#333' : '#f0f0f0',
                                        color: mode === 'dark' ? '#fff' : '#333',
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}
                            </div>
                          </Tooltip>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}

                {questionsList.map(
                  (q, index) =>
                    !q.submitted && index === currentQuestionIndex && (
                      <div
                        key={index}
                        className="question-container"
                        style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                      >
                        <p
                          style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                        >
                          {createBlanks(
                            q.currentQuestion,
                            q.answers,
                            index === currentQuestionIndex
                          )}
                        </p>

                        {index === currentQuestionIndex && (
                          <Card
                            sx={{
                              mb: 2,
                              borderRadius: "12px",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                              backgroundColor: mode === "dark" ? "#1E1E2E" : "#fff",
                              border: "1px solid",
                              borderColor: mode === "dark" ? "#333" : "#e0e7ff",
                            }}
                          >
                            <CardContent>
                              <div>
                                Explanation for Question (optional)
                                <ReactQuill
                                  id="explanationTextField"
                                  variant="outlined"
                                  value={currentExplanation}
                                  onChange={setCurrentExplanation}
                                  onMouseUp={handleExplanationSelect}
                                  placeholder="Enter your explanation here"
                                  modules={{
                                    toolbar: [
                                      ["bold", "italic", "underline", "strike"], // toggled buttons
                                      ["blockquote", "code-block"],
                                      [{ header: 1 }, { header: 2 }], // custom button values
                                      [{ list: "ordered" }, { list: "bullet" }],
                                      [{ script: "sub" }, { script: "super" }], // superscript/subscript
                                      [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
                                      [{ direction: "rtl" }], // text direction
                                      [{ size: ["small", false, "large", "huge"] }], // custom dropdown
                                      [{ header: [1, 2, 3, 4, 5, 6, false] }],
                                      [{ color: [] }, { background: [] }], // dropdown with defaults from theme
                                      [{ font: [] }],
                                      [{ align: [] }],
                                      ["clean"], // remove formatting button
                                    ],
                                  }}
                                  formats={[
                                    "header",
                                    "font",
                                    "size",
                                    "bold",
                                    "italic",
                                    "underline",
                                    "strike",
                                    "blockquote",
                                    "code-block",
                                    "list",
                                    "bullet",
                                    "indent",
                                    "link",
                                    "image",
                                    "video",
                                    "color",
                                    "background",
                                    "align",
                                    "script",
                                    { name: "preserve-spaces", type: "inline" },
                                  ]}
                                  InputProps={{
                                    style: {
                                      wordBreak: "break-word",
                                      overflowWrap: "break-word",
                                      whiteSpace: "pre-wrap",
                                    },
                                  }}
                                />
                                <div
                                  className="mt-2"
                                  style={{
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                  }}
                                >
                                  {renderContent(currentExplanation)}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    )
                )}
                <div
                  onClick={() => {
                    if (!permissions.can_create_fib && !permissions.is_admin) {
                      showSnackbar(
                        "You don't have permission to create FIB Questions.",
                        "warning"
                      );
                      return;
                    }
                    handleAnswersSubmit();
                  }}
                  style={{

                    display: "inline-block",
                    cursor:
                      permissions.can_create_fib || permissions.is_admin
                        ? "pointer"
                        : "not-allowed",
                    marginBottom: "24px", // Add spacing after the button
                  }}
                >
                  <Button
                    variant="contained"
                    disabled={
                      !(permissions.can_create_fib || permissions.is_admin) ||
                      isLoadingPermissions
                    }
                    size="small"
                    sx={{
                      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                      color: "white",
                      padding: "6px 16px",     // reduced size
                      fontSize: "0.85rem",     // smaller text
                      fontWeight: "700",
                      borderRadius: "8px",     // slightly smaller radius
                      textTransform: "none",

                      boxShadow: "0 8px 25px rgba(30, 58, 138, 0.4)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)",
                        transform: "translateY(-3px)",
                        boxShadow: "0 12px 35px rgba(30, 58, 138, 0.5)",
                      },
                      "&.Mui-disabled": {
                        background: "#d1d5db",
                        color: "white",
                        opacity: 0.6,
                      },
                    }}
                    startIcon={<AddIcon></AddIcon>}
                  >
                    ADD FLASHCARD
                  </Button>
                </div>
                {permissions.can_read_fib || permissions.is_admin ? (
                  <div>
                    <ul className="card-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {cards.map((card, index) => (
                        <li
                          key={index}
                          style={{
                            position: "relative",
                            padding: "16px",
                            borderRadius: "16px",
                            marginBottom: "16px",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            maxWidth: "100%",
                            whiteSpace: "pre-wrap",
                            border: mode === "dark" ? "1px solid #333" : "1px solid #e0e7ff",
                            backgroundColor: mode === "dark" ? "#2A2A3E" : "#fff",
                            boxShadow: mode === "dark" ? "0 4px 14px rgba(0,0,0,0.6)" : "0 4px 12px rgba(15,23,42,0.08)",
                            transition: "transform 0.3s ease, box-shadow 0.3s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-4px)";
                            e.currentTarget.style.boxShadow = mode === "dark"
                              ? "0 8px 30px rgba(0,0,0,0.75)"
                              : "0 8px 30px rgba(102, 126, 234, 0.2)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = mode === "dark"
                              ? "0 4px 14px rgba(0,0,0,0.6)"
                              : "0 4px 12px rgba(15,23,42,0.08)";
                          }}
                        >
                          {/* Question Section */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              paddingRight: "40px",
                              maxWidth: "calc(100% - 70px)",
                              whiteSpace: "pre-wrap",
                              marginBottom: "16px",
                            }}
                          >
                            <div
                              style={{
                                background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                                color: "white",
                                padding: "8px 14px",
                                borderRadius: "10px",
                                fontWeight: "800",
                                fontSize: "1.1rem",
                                marginRight: "16px",
                                minWidth: "45px",
                                textAlign: "center",
                                boxShadow: "0 4px 15px rgba(30, 58, 138, 0.3)",
                              }}
                            >
                              Q.{index + 1}
                            </div>
                            <span style={{ flex: 1, display: "flex", alignItems: "center" }}>
                              <strong>
                                {renderContent(
                                  card.question
                                    .replace(/\[BLANK\]/g, "_____")
                                    .replace(/_____(?=_____)/g, "_____, ")
                                )}
                              </strong>
                            </span>
                          </div>

                          {/* Icons Section */}
                          <div
                            style={{
                              position: "absolute",
                              top: "20px",
                              right: "20px",
                              display: "flex",
                              gap: "14px",
                              zIndex: 10,
                              padding: "10px 14px",
                              borderRadius: "12px",
                              backdropFilter: "blur(10px)",
                            }}
                          >
                            <i
                              className={`bi ${card.expanded ? "bi-arrows-angle-contract" : "bi-arrows-angle-expand"}`}
                              title={card.expanded ? "Contract" : "Expand"}
                              onClick={() => toggleCardExpand(index)}
                              style={{
                                cursor: "pointer",
                                fontSize: "1.2rem",
                                color: "#667eea",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = "#764ba2";
                                e.currentTarget.style.transform = "scale(1.2)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = "#667eea";
                                e.currentTarget.style.transform = "scale(1)";
                              }}
                            ></i>
                            {/* ── NEW VIEW ICON ── */}
                            <Tooltip title="View Flashcard">
                              <i
                                className="bi bi-eye"
                                onClick={() => openViewModal(card)}
                                style={{
                                  cursor: "pointer",
                                  fontSize: "1.2rem",
                                  color: "#0ea5e9",           // nice blue
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = "#0284c7";
                                  e.currentTarget.style.transform = "scale(1.2)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = "#0ea5e9";
                                  e.currentTarget.style.transform = "scale(1)";
                                }}
                              ></i>
                            </Tooltip>
                            <i
                              className="bi bi-pencil-square"
                              title="Edit"
                              onClick={() => openEditDialog(card)}
                              style={{
                                cursor: permissions.can_update_fib || permissions.is_admin ? "pointer" : "not-allowed",
                                opacity: permissions.can_update_fib || permissions.is_admin ? 1 : 0.4,
                                fontSize: "1.2rem",
                                color: "#10b981",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                if (permissions.can_update_fib || permissions.is_admin) {
                                  e.currentTarget.style.color = "#059669";
                                  e.currentTarget.style.transform = "scale(1.2)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = "#10b981";
                                e.currentTarget.style.transform = "scale(1)";
                              }}
                            ></i>
                            <i
                              className="bi bi-trash3"
                              title="Delete"
                              onClick={() => openDeleteDialog(card.id)}
                              style={{
                                cursor: permissions.can_delete_fib || permissions.is_admin ? "pointer" : "not-allowed",
                                opacity: permissions.can_delete_fib || permissions.is_admin ? 1 : 0.4,
                                fontSize: "1.2rem",
                                color: "#ef4444",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                if (permissions.can_delete_fib || permissions.is_admin) {
                                  e.currentTarget.style.color = "#dc2626";
                                  e.currentTarget.style.transform = "scale(1.2)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = "#ef4444";
                                e.currentTarget.style.transform = "scale(1)";
                              }}
                            ></i>
                            <i
                              className="bi bi-tags"
                              title="Tag"
                              onClick={(e) => openTagModal(index, e)}
                              style={{
                                cursor: "pointer",
                                opacity: 1,
                                fontSize: "1.2rem",
                                color: "#8b5cf6",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = "#7c3aed";
                                e.currentTarget.style.transform = "scale(1.2)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = "#8b5cf6";
                                e.currentTarget.style.transform = "scale(1)";
                              }}
                            ></i>
                          </div>

                          {/* Expanded Content */}
                          {card.expanded && (permissions.can_read_fib || permissions.is_admin) && (
                            <>
                              {/* Answers Section */}
                              <div style={{ marginBottom: "16px" }}>
                                {card.answer.map((ans, idx) => (
                                  <div
                                    key={idx}
                                    style={{
                                      marginBottom: "12px",
                                      padding: "12px",
                                      borderRadius: "10px",
                                      border: ans.is_correct
                                        ? "1px solid rgba(22, 163, 74, 0.3)"
                                        : "1px solid rgba(148, 163, 184, 0.45)",
                                      backgroundColor: ans.is_correct
                                        ? mode === "dark"
                                          ? "rgba(22, 163, 74, 0.2)"
                                          : "rgba(220, 252, 231, 0.6)"
                                        : mode === "dark"
                                          ? "rgba(15, 23, 42, 0.5)"
                                          : "#f8fafc",
                                    }}
                                  >
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                      <span
                                        style={{
                                          fontSize: "0.8rem",
                                          fontWeight: 600,
                                          textTransform: "uppercase",
                                          color: ans.is_correct ? "#16a34a" : "#64748b",
                                        }}
                                      >
                                        {ans.is_correct ? "✓ Correct" : "✗ Incorrect"}
                                      </span>
                                      <span style={{ fontWeight: ans.is_correct ? 600 : 400 }}>
                                        {renderContent(ans.answer)}
                                      </span>
                                    </div>
                                    {ans.explanation && (
                                      <div
                                        style={{
                                          marginTop: "8px",
                                          paddingLeft: "8px",
                                          borderLeft: "2px solid rgba(148, 163, 184, 0.5)",
                                          fontSize: "0.9rem",
                                          color: mode === "dark" ? "#e5e7eb" : "#4b5563",
                                        }}
                                      >
                                        {renderContent(ans.explanation)}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>

                              {/* Explanation Section */}
                              {card.explanation && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                    whiteSpace: "pre-wrap",
                                    marginBottom: "16px",
                                  }}
                                >
                                  <div
                                    style={{
                                      background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                      color: "white",
                                      padding: "6px 12px",
                                      borderRadius: "8px",
                                      fontWeight: "700",
                                      fontSize: "0.9rem",
                                      marginRight: "14px",
                                      minWidth: "fit-content",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    💡 Explanation
                                  </div>
                                  <span style={{ flex: 1 }}>
                                    {renderContent(card.explanation)}
                                  </span>
                                </div>
                              )}

                              {/* Tags Section */}
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                {card.tags?.map((tag) => (
                                  <span
                                    key={tag.id}
                                    style={{
                                      padding: "4px 8px",
                                      borderRadius: "999px",
                                      fontSize: "0.8rem",
                                      backgroundColor: mode === "dark" ? "#333" : "#f0f0f0",
                                      color: mode === "dark" ? "#fff" : "#333",
                                      border: mode === "dark" ? "1px solid #555" : "1px solid #ddd",
                                    }}
                                  >
                                    #{tag.name}
                                  </span>
                                ))}
                              </div>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", marginTop: "20px" }}>
                    You don't have permission to view FIB Questions.
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Simple header */}
                <Typography
                  variant="h4"
                  sx={{
                    mb: 3,
                    fontWeight: '800',
                    background: mode === "dark" ? "linear-gradient(135deg, #f0f7ff 0%, #e0e7ff 100%)" : mode === "custom" ? "#f5f5f5" || "#222222" : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: 'text'
                  }}
                >
                  Fill in the Blanks Questions
                </Typography>

                {/* Read-only flashcard list */}
                <ul className="card-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {cards.length > 0 ? (
                    cards.map((card, index) => (
                      <li
                        key={index}
                        style={{
                          position: "relative",
                          padding: "16px",
                          borderRadius: "16px",
                          marginBottom: "16px",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          maxWidth: "100%",
                          whiteSpace: "pre-wrap",
                          border: mode === "dark" ? "1px solid #333" : "1px solid #e0e7ff",
                          backgroundColor: mode === "dark" ? "#2A2A3E" : "#fff",
                          boxShadow: mode === "dark" ? "0 4px 14px rgba(0,0,0,0.6)" : "0 4px 12px rgba(15,23,42,0.08)",
                          transition: "transform 0.3s ease, box-shadow 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-4px)";
                          e.currentTarget.style.boxShadow = mode === "dark"
                            ? "0 8px 30px rgba(0,0,0,0.75)"
                            : "0 8px 30px rgba(102, 126, 234, 0.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = mode === "dark"
                            ? "0 4px 14px rgba(0,0,0,0.6)"
                            : "0 4px 12px rgba(15,23,42,0.08)";
                        }}
                      >
                        {/* Question */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            paddingRight: "40px",
                            maxWidth: "calc(100% - 70px)",
                            whiteSpace: "pre-wrap",
                            marginBottom: "16px",
                          }}
                        >
                          <div
                            style={{
                              background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                              color: "white",
                              padding: "8px 14px",
                              borderRadius: "10px",
                              fontWeight: "800",
                              fontSize: "1.1rem",
                              marginRight: "16px",
                              minWidth: "45px",
                              textAlign: "center",
                              boxShadow: "0 4px 15px rgba(30, 58, 138, 0.3)",
                            }}
                          >
                            Q
                          </div>
                          <span style={{ flex: 1, display: "flex", alignItems: "center" }}>
                            <strong>
                              {renderContent(
                                card.question
                                  .replace(/\[BLANK\]/g, "_____")
                                  .replace(/_____(?=_____)/g, "_____, ")
                              )}
                            </strong>
                          </span>
                        </div>

                        {/* Expand/Collapse Icon ONLY */}
                        <div
                          style={{
                            position: "absolute",
                            top: "20px",
                            right: "20px",
                            display: "flex",
                            gap: "14px",
                            zIndex: 10,
                            padding: "10px 14px",
                            borderRadius: "12px",
                            backdropFilter: "blur(10px)",
                          }}
                        >
                          <i
                            className={`bi ${card.expanded ? "bi-arrows-angle-contract" : "bi-arrows-angle-expand"}`}
                            title={card.expanded ? "Contract" : "Expand"}
                            onClick={() => toggleCardExpand(index)}
                            style={{
                              cursor: "pointer",
                              fontSize: "1.2rem",
                              color: "#667eea",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = "#764ba2";
                              e.currentTarget.style.transform = "scale(1.2)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = "#667eea";
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          ></i>
                        </div>

                        {/* Expanded content - Answers and Explanation ONLY */}
                        {card.expanded && (
                          <>
                            {/* Answers Section */}
                            <div style={{ marginBottom: "16px" }}>
                              {card.answer.map((ans, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    marginBottom: "12px",
                                    padding: "12px",
                                    borderRadius: "10px",
                                    border: ans.is_correct
                                      ? "1px solid rgba(22, 163, 74, 0.3)"
                                      : "1px solid rgba(148, 163, 184, 0.45)",
                                    backgroundColor: ans.is_correct
                                      ? mode === "dark"
                                        ? "rgba(22, 163, 74, 0.2)"
                                        : "rgba(220, 252, 231, 0.6)"
                                      : mode === "dark"
                                        ? "rgba(15, 23, 42, 0.5)"
                                        : "#f8fafc",
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <span
                                      style={{
                                        fontSize: "0.8rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: ans.is_correct ? "#16a34a" : "#64748b",
                                      }}
                                    >
                                      {ans.is_correct ? "✓ Correct" : "✗ Incorrect"}
                                    </span>
                                    <span style={{ fontWeight: ans.is_correct ? 600 : 400 }}>
                                      {renderContent(ans.answer)}
                                    </span>
                                  </div>
                                  {ans.explanation && (
                                    <div
                                      style={{
                                        marginTop: "8px",
                                        paddingLeft: "8px",
                                        borderLeft: "2px solid rgba(148, 163, 184, 0.5)",
                                        fontSize: "0.9rem",
                                        color: mode === "dark" ? "#e5e7eb" : "#4b5563",
                                      }}
                                    >
                                      {renderContent(ans.explanation)}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* Explanation Section */}
                            {card.explanation && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                  marginBottom: "16px",
                                }}
                              >
                                <div
                                  style={{
                                    background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                    color: "white",
                                    padding: "6px 12px",
                                    borderRadius: "8px",
                                    fontWeight: "700",
                                    fontSize: "0.9rem",
                                    marginRight: "14px",
                                    minWidth: "fit-content",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  💡 Explanation
                                </div>
                                <span style={{ flex: 1 }}>
                                  {renderContent(card.explanation)}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </li>
                    ))
                  ) : (
                    <li style={{ padding: "16px" }}>
                      <Typography>No Questions Available</Typography>
                    </li>)}
                </ul>
              </>
            )}
            <Dialog
              open={deleteDialogOpen}
              onClose={handleCloseDeleteDialog}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: '20px',
                  background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.cardBg : '#ffffff',
                  boxShadow: mode === 'dark'
                    ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                    : '0 8px 32px rgba(239, 68, 68, 0.2)',
                  overflow: 'hidden',
                }
              }}
            >
              {/* Warning Header */}
              <Box sx={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                p: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: 'white',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {/* <i className="bi bi-exclamation-triangle" style={{ fontSize: 28 }}></i> */}
                    <i className="bi bi-trash3" style={{
                      fontSize: 40,
                      // color: '#ef4444',
                    }}></i>

                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: '700', mb: 0.5 }}>
                      Delete Flashcard
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      This action cannot be undone
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Content */}
              <DialogContent sx={{
                p: 4,
                background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.background : '#ffffff',
              }}>
                <Box sx={{
                  textAlign: 'center',
                  py: 2,
                }}>
                  {/* <Box sx={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: mode === 'dark'
          ? 'rgba(239, 68, 68, 0.1)'
          : 'rgba(239, 68, 68, 0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
      }}>
       
      </Box> */}
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#000',
                      fontWeight: 600,
                    }}
                  >
                    Are you sure you want to delete this flashcard?
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: mode === 'dark' ? '#A0A0A6' : mode === 'custom' ? customColors.textSecondary : '#6B7280',
                      lineHeight: 1.6,
                    }}
                  >
                    This will permanently remove the flashcard and all associated data. This action cannot be reversed.
                  </Typography>
                </Box>
              </DialogContent>

              {/* Actions */}
              <DialogActions sx={{
                p: 3,
                gap: 2,
                background: mode === 'dark' ? '#2A2A3E' : mode === 'custom' ? customColors.background : '#f8f9fa',
              }}>
                <Button
                  onClick={handleCloseDeleteDialog}
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: mode === 'dark' ? '#555' : mode === 'custom' ? customColors.border : '#d1d5db',
                    color: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#000',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#000',
                      background: 'transparent',
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteCard}
                  variant="contained"
                  fullWidth
                  disabled={!(permissions.can_delete_fib || permissions.is_admin)}
                  sx={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(239, 68, 68, 0.4)',
                    },
                    '&.Mui-disabled': {
                      background: '#d1d5db',
                      color: 'white',
                      opacity: 0.6,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Delete Flashcard
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={editDialogOpen}
              onClose={handleCloseEditDialog}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: '16px',
                  background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.cardBg : '#ffffff',
                  boxShadow: mode === 'dark'
                    ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                    : '0 8px 32px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                }
              }}
            >
              {/* Gradient Header */}
              <Box sx={{
                background: mode === 'dark'
                  ? 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)'
                  : mode === 'custom'
                    ? customColors.gradient
                    : 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
                p: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: 'white',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <i className="bi bi-pencil-square" style={{ fontSize: 28 }}></i>
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: '700', mb: 0.5 }}>
                      Edit Flashcard
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Update your flashcard details below
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Content with improved styling */}
              <DialogContent sx={{
                p: 4,
                background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.background : '#ffffff',
                "&::-webkit-scrollbar": {
                  width: "10px",
                },
                "&::-webkit-scrollbar-track": {
                  background: mode === "dark" ? "#2d3748" : "#f1f5f9",
                  borderRadius: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: mode === "dark" ? "#475569" : "#94a3b8",
                  borderRadius: "6px",
                  border: `2px solid ${mode === "dark" ? "#2d3748" : "#f1f5f9"}`,
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#64748b",
                },
                scrollbarWidth: "thin",
                scrollbarColor: mode === "dark"
                  ? "#475569 #2d3748"
                  : "#94a3b8 #f1f5f9",
              }}
              >
                {/* Question Field */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1.5,
                      fontWeight: 600,
                      color: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#000',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
                    }} />
                    Question <span style={{ color: '#ef4444' }}>*</span>
                  </Typography>
                  <Box className={`editor-wrapper ${mode}-mode`} sx={{ position: 'relative' }}>
                    <ReactQuill
                      value={editQuestion}
                      onChange={setEditQuestion}
                      modules={{
                        toolbar: [
                          ['bold', 'italic', 'underline', 'strike'],
                          ['blockquote', 'code-block'],
                          [{ header: 1 }, { header: 2 }],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                          [{ script: 'sub' }, { script: 'super' }],
                          [{ indent: '-1' }, { indent: '+1' }],
                          [{ size: ['small', false, 'large', 'huge'] }],
                          [{ color: [] }, { background: [] }],
                          [{ align: [] }],
                          ['clean'],
                        ],
                      }}
                      formats={[
                        "header", "font", "size", "bold", "italic", "underline", "strike",
                        "blockquote", "code-block", "list", "bullet", "indent", "link",
                        "image", "video", "color", "background", "align", "script",
                      ]}
                    />
                  </Box>
                </Box>

                {/* Answer Options */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1.5,
                      fontWeight: 600,
                      color: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#000',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    }} />
                    Answer Options <span style={{ color: '#ef4444' }}>*</span>
                  </Typography>
                  {editOptions.map((option, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: "20px",
                        padding: "10px",
                        border: mode === 'dark' ? "1px solid #d1bfbfff" : mode === 'custom' ? `1px solid ${customColors.bgNav}` : "1px solid #ddd",
                        borderRadius: "6px",
                      }}
                    >
                      <div style={{ marginBottom: "5px", fontWeight: "bold" }}>
                        Option {index + 1}
                      </div>
                      <div style={{ marginBottom: "20px" }}>
                        <RichInlineEditor
                          value={option}
                          onChange={(val) => handleEditOptionChange(index, val)}
                          placeholder="Supports LaTeX, bold, H₂O, √x, etc."
                        />
                      </div>
                      <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                        <label style={{ marginRight: "30px", display: "flex", alignItems: "center" }}>
                          <input
                            type="checkbox"
                            checked={editIsCorrect[index]}
                            onChange={(e) => handleEditCorrectChange(index, e.target.checked)}
                            style={{ marginRight: "8px" }}
                          />
                          Correct Answer
                        </label>
                        <IconButton aria-label="delete" onClick={() => handleEditRemoveOption(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </div>
                      <div style={{ marginBottom: "5px", fontWeight: "bold" }}>
                        Explanation
                      </div>
                      <div style={{ marginBottom: "20px" }}>
                        <RichInlineEditor
                          value={editOptionExplanations[index]}
                          onChange={(val) => handleEditExplanationChange(index, val)}
                          placeholder="Explain why this option is correct or incorrect"
                        />
                      </div>
                    </div>
                  ))}
                  <Button onClick={addEditOption} variant="contained" color="primary">
                    Add Option
                  </Button>
                </Box>

                {/* Explanation Field */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1.5,
                      fontWeight: 600,
                      color: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#000',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    }} />
                    Explanation
                  </Typography>
                  <Box className={`editor-wrapper ${mode}-mode`} sx={{ position: 'relative' }}>
                    <ReactQuill
                      value={editExplanation}
                      onChange={setEditExplanation}
                      modules={{
                        toolbar: [
                          ['bold', 'italic', 'underline', 'strike'],
                          ['blockquote', 'code-block'],
                          [{ header: 1 }, { header: 2 }],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                          [{ script: 'sub' }, { script: 'super' }],
                          [{ indent: '-1' }, { indent: '+1' }],
                          [{ size: ['small', false, 'large', 'huge'] }],
                          [{ color: [] }, { background: [] }],
                          [{ align: [] }],
                          ['clean'],
                        ],
                      }}
                      formats={[
                        "header", "font", "size", "bold", "italic", "underline", "strike",
                        "blockquote", "code-block", "list", "bullet", "indent", "link",
                        "image", "video", "color", "background", "align", "script",
                      ]}
                    />
                  </Box>
                </Box>
              </DialogContent>

              {/* Enhanced Actions */}
              <DialogActions sx={{
                p: 3,
                gap: 2,
                background: mode === 'dark' ? '#2A2A3E' : mode === 'custom' ? customColors.background : '#f8f9fa',
              }}>
                <Button
                  onClick={handleCloseEditDialog}
                  variant="outlined"
                  sx={{
                    borderColor: mode === 'dark' ? '#555' : mode === 'custom' ? customColors.border : '#d1d5db',
                    color: mode === 'dark' ? '#A0A0A6' : mode === 'custom' ? customColors.textSecondary : '#6B7280',
                    '&:hover': {
                      borderColor: mode === 'dark' ? '#fff' : mode === 'custom' ? customColors.text : '#000',
                      background: 'transparent',
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateQuestion}
                  variant="contained"
                  disabled={!(permissions.can_update_fib || permissions.is_admin)}
                  sx={{
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
                    color: 'white',
                    fontWeight: 600,
                    px: 4,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(30, 58, 138, 0.3)',
                    },
                    '&.Mui-disabled': {
                      background: '#d1d5db',
                      color: 'white',
                      opacity: 0.6,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Save Changes
                </Button>
              </DialogActions>
            </Dialog>

            <Snackbar
              open={snackbarOpen}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Alert
                onClose={handleSnackbarClose}
                severity={snackbarSeverity}
                sx={{ width: "100%" }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>
            {/* Duplicate question dialog */}
            <Dialog open={duplicateDialogOpen} onClose={handleCancelDuplicate}>
              <DialogTitle
                sx={{
                  background:
                    mode === "dark"
                      ? "linear-gradient(to right, #142be1, #44a6e9)"
                      : mode === "custom"
                        ? customColors.background
                        : "linear-gradient(to right, #142be1, #44a6e9)",
                }}
              >
                Duplicate Question
              </DialogTitle>
              <DialogContent>
                <DialogContentText
                  sx={{
                    color:
                      mode === "dark"
                        ? "#fff"
                        : mode === "custom"
                          ? customColors.text
                          : "#000",
                  }}
                >
                  A flashcard with this question already exists. Do you want to add it
                  anyway?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancelDuplicate}>Cancel</Button>
                <Button
                  onClick={handleConfirmDuplicate}
                  disabled={!(permissions.can_create_fib || permissions.is_admin)}
                  sx={{
                    backgroundColor:
                      mode === "dark"
                        ? "#142be1"
                        : mode === "custom"
                          ? customColors.primary
                          : "#44a6e9",
                    "&:hover": {
                      backgroundColor:
                        mode === "dark"
                          ? "#0d1abf"
                          : mode === "custom"
                            ? customColors.background
                            : "#0d1abf",
                    },
                  }}
                  variant="contained"
                  color="primary"
                >
                  Add Anyway
                </Button>
              </DialogActions>
            </Dialog>
            <TagModal
              open={tagModalOpen}
              position={tagModalPosition}
              onClose={() => setTagModalOpen(false)}
              subfolderId={subfolder_id}
              cardId={cards[tagCardIndex]?.id}
              cardQuestionType={cards[tagCardIndex]?.question_type}
              onTagAdded={handleTagAdded}
              onTagUpdated={handleTagUpdated}
              onTagDeleted={handleTagDeleted}
              onShowSnackbar={showSnackbar}
            />
            <Dialog
              open={userGuideOpen}
              onClose={() => setUserGuideOpen(false)}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: "16px",
                  boxShadow: mode === "dark"
                    ? "0 8px 32px rgba(0, 0, 0, 0.4)"
                    : "0 8px 32px rgba(30, 139, 175, 0.15)",
                  overflow: "hidden",
                  background: mode === "dark"
                    ? "#1E1B18"
                    : mode === "custom"
                      ? customColors.bg
                      : "#ffffff",
                },
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                  p: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  color: "white",
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: "700", mb: 0.5 }}>
                    User Guide
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Fill in the Blanks Questions
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setUserGuideOpen(false)}
                  sx={{
                    color: "white",
                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Content */}
              <DialogContent
                sx={{
                  p: 4,
                  overflowY: "auto",
                  maxHeight: "75vh",
                  "&::-webkit-scrollbar": {
                    width: "10px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: mode === "dark"
                      ? "#2d3748"
                      : mode === "custom"
                        ? customColors.scrollTrack
                        : "#f1f5f9",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: mode === "dark"
                      ? "#475569"
                      : mode === "custom"
                        ? customColors.scrollThumb
                        : "#94a3b8",
                    borderRadius: "6px",
                    border: `2px solid ${mode === "dark"
                      ? "#2d3748"
                      : mode === "custom"
                        ? customColors.border
                        : "#f1f5f9"
                      }`,
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: mode === "dark"
                      ? "#64748b"
                      : mode === "custom"
                        ? customColors.scrollThumbHover
                        : "#475569",
                  },
                  scrollbarWidth: "thin",
                  scrollbarColor: mode === "dark"
                    ? "#475569 #2d3748"
                    : mode === "custom"
                      ? `${customColors.scrollThumb} ${customColors.scrollTrack}`
                      : "#94a3b8 #f1f5f9",
                  background: mode === "dark"
                    ? "#1E1B18"
                    : mode === "custom"
                      ? customColors.bg
                      : "#ffffff",
                  color: mode === "dark"
                    ? "rgba(255, 255, 255, 0.87)"
                    : mode === "custom"
                      ? customColors.text
                      : "rgba(30, 27, 24, 0.87)",
                }}
              >
                {/* Question Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <i className="bi bi-card-text" style={{ color: "white", fontSize: 20 }}></i>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "600",
                        color: mode === "dark"
                          ? "#e0e0e0"
                          : mode === "custom"
                            ? customColors.text
                            : "#1E1B18",
                      }}
                    >
                      Question
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: mode === "dark"
                        ? "rgba(255, 255, 255, 0.7)"
                        : mode === "custom"
                          ? customColors.textSecondary
                          : "rgba(30, 27, 24, 0.7)",
                      lineHeight: 1.7,
                      pl: 7,
                    }}
                  >
                    Enter your question in the <strong>Question</strong> field. Use <strong>[BLANK]</strong> to mark where the user should fill in the answer. You can use rich text formatting, including bold, italics, and LaTeX for mathematical expressions.
                  </Typography>
                </Box>

                {/* Answer Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <i className="bi bi-chat-text" style={{ color: "white", fontSize: 20 }}></i>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "600",
                        color: mode === "dark"
                          ? "#e0e0e0"
                          : mode === "custom"
                            ? customColors.text
                            : "#1E1B18",
                      }}
                    >
                      Answer Options
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: mode === "dark"
                        ? "rgba(255, 255, 255, 0.7)"
                        : mode === "custom"
                          ? customColors.textSecondary
                          : "rgba(30, 27, 24, 0.7)",
                      lineHeight: 1.7,
                      pl: 7,
                    }}
                  >
                    For each <strong>[BLANK]</strong> in your question, provide possible answers. You can add multiple options for each blank and mark the correct one(s). You can also add explanations for each option.
                  </Typography>
                </Box>

                {/* Tags Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <i className="bi bi-tags" style={{ color: "white", fontSize: 20 }}></i>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "600",
                        color: mode === "dark"
                          ? "#e0e0e0"
                          : mode === "custom"
                            ? customColors.text
                            : "#1E1B18",
                      }}
                    >
                      Tags (Mandatory)
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: mode === "dark"
                        ? "rgba(255, 255, 255, 0.7)"
                        : mode === "custom"
                          ? customColors.textSecondary
                          : "rgba(30, 27, 24, 0.7)",
                      lineHeight: 1.7,
                      pl: 7,
                    }}
                  >
                    Add tags to organize your questions. This is mandatory - you must add at least one tag before saving.
                  </Typography>
                </Box>

                {/* Explanation Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <i className="bi bi-lightbulb" style={{ color: "white", fontSize: 20 }}></i>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "600",
                        color: mode === "dark"
                          ? "#e0e0e0"
                          : mode === "custom"
                            ? customColors.text
                            : "#1E1B18",
                      }}
                    >
                      Explanation
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: mode === "dark"
                        ? "rgba(255, 255, 255, 0.7)"
                        : mode === "custom"
                          ? customColors.textSecondary
                          : "rgba(30, 27, 24, 0.7)",
                      lineHeight: 1.7,
                      pl: 7,
                    }}
                  >
                    Optionally, add an explanation for the question or for each answer option. This can provide additional context or details about the answer.
                  </Typography>
                </Box>

                {/* Add Flashcard Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <AddIcon sx={{ color: "white", fontSize: 20 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "600",
                        color: mode === "dark"
                          ? "#e0e0e0"
                          : mode === "custom"
                            ? customColors.text
                            : "#1E1B18",
                      }}
                    >
                      Add Flashcard
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: mode === "dark"
                        ? "rgba(255, 255, 255, 0.7)"
                        : mode === "custom"
                          ? customColors.textSecondary
                          : "rgba(30, 27, 24, 0.7)",
                      lineHeight: 1.7,
                      pl: 7,
                    }}
                  >
                    Click the <strong>Add Flashcard</strong> button to save your question, answer options, tags, and explanation. If the question already exists, you'll be prompted to confirm.
                  </Typography>
                </Box>

                {/* Flashcard List Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <i className="bi bi-card-list" style={{ color: "white", fontSize: 20 }}></i>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "600",
                        color: mode === "dark"
                          ? "#e0e0e0"
                          : mode === "custom"
                            ? customColors.text
                            : "#1E1B18",
                      }}
                    >
                      Flashcard List
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: mode === "dark"
                        ? "rgba(255, 255, 255, 0.7)"
                        : mode === "custom"
                          ? customColors.textSecondary
                          : "rgba(30, 27, 24, 0.7)",
                      lineHeight: 1.7,
                      pl: 7,
                    }}
                  >
                    View your list of flashcards below. You can <strong>expand</strong>, <strong>edit</strong>, <strong>delete</strong>, or <strong>tag</strong> each flashcard.
                  </Typography>
                </Box>

                {/* Pro Tips */}
                <Box
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    bgcolor: mode === "dark"
                      ? "rgba(144, 202, 249, 0.05)"
                      : mode === "custom"
                        ? customColors.bgSecondary
                        : "rgba(30, 139, 175, 0.05)",
                    border: mode === "dark"
                      ? "1px solid rgba(144, 202, 249, 0.2)"
                      : mode === "custom"
                        ? `1px solid ${customColors.border}`
                        : "1px solid rgba(30, 139, 175, 0.2)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: "600",
                      color: mode === "dark"
                        ? "#90caf9"
                        : mode === "custom"
                          ? customColors.text
                          : "#1e8bafff",
                      mb: 2,
                    }}
                  >
                    💡 Quick Tips
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: mode === "dark"
                          ? "rgba(255, 255, 255, 0.7)"
                          : mode === "custom"
                            ? customColors.textSecondary
                            : "rgba(30, 27, 24, 0.7)",
                        lineHeight: 1.7,
                      }}
                    >
                      • Use <strong>[BLANK]</strong> to mark where the user should fill in the answer.
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: mode === "dark"
                          ? "rgba(255, 255, 255, 0.7)"
                          : mode === "custom"
                            ? customColors.textSecondary
                            : "rgba(30, 27, 24, 0.7)",
                        lineHeight: 1.7,
                      }}
                    >
                      • Use LaTeX for mathematical expressions by enclosing them in `$...$` or `$$...$$`.
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: mode === "dark"
                          ? "rgba(255, 255, 255, 0.7)"
                          : mode === "custom"
                            ? customColors.textSecondary
                            : "rgba(30, 27, 24, 0.7)",
                        lineHeight: 1.7,
                      }}
                    >
                      • Click the <strong>Expand/Collapse</strong> icon to show or hide flashcard details.
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: mode === "dark"
                          ? "rgba(255, 255, 255, 0.7)"
                          : mode === "custom"
                            ? customColors.textSecondary
                            : "rgba(30, 27, 24, 0.7)",
                        lineHeight: 1.7,
                      }}
                    >
                      • <strong>Tags are mandatory</strong> - you must add at least one tag before saving.
                    </Typography>
                  </Box>
                </Box>
              </DialogContent>
            </Dialog>

            {/* Dialog for viewing flashcard details */}
            <Dialog
              open={viewModalOpen}
              onClose={() => setViewModalOpen(false)}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 18,
                  overflow: "hidden",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                  background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                }
              }}
            >
              <Box
                sx={{
                  minHeight: 520,
                  display: "flex",
                  flexDirection: "column",
                  p: { xs: 2, sm: 4 },
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    px: 2,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.2)",
                      color: "#fff",
                      padding: "8px 24px",
                      borderRadius: 20,
                      fontWeight: "bold",
                      fontSize: "0.95rem",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    Question 1 of 1
                  </Box>

                  <IconButton
                    onClick={() => setViewModalOpen(false)}
                    sx={{
                      color: "#fff",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
                    }}
                  >
                    <CloseIcon fontSize="large" />
                  </IconButton>
                </Box>

                {/* White content area – scrollbar added here */}
                <Box
                  sx={{
                    backgroundColor: "#ffffff",
                    borderRadius: 16,
                    p: { xs: 3, sm: 5 },
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "inset 0 2px 12px rgba(0,0,0,0.08)",
                    color: "#000000",
                    overflowY: "auto",                      // ← Scrollbar enabled
                    maxHeight: "75vh",                      // ← Limits height so scroll appears when needed
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#888",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#555",
                    },
                  }}
                >
                  {/* Question */}
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      mb: 5,
                      lineHeight: 1.5,
                      textAlign: "center",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {viewedCard &&
                      renderContent(
                        viewedCard.question
                          .replace(/\[BLANK\]/g, "______")
                      )}
                  </Typography>

                  {/* Options */}
                  {/* <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 3,
                      mb: 6,
                    }}
                  >
                    {viewedCard?.answer?.map((option, idx) => (
                      <Button
                        key={idx}
                        disableRipple
                        sx={{
                          textAlign: "center",
                          justifyContent: "center",
                          padding: "18px 24px",
                          borderRadius: 12,
                          border: "2px solid #e0e0e0",
                          backgroundColor: "#f8f9fa",
                          color: "#333",
                          textTransform: "none",
                          fontSize: "1.3rem",
                          fontWeight: 600,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          transition: "all 0.3s",
                          "&:hover": {
                            backgroundColor: "#e3f2fd",
                            borderColor: "#1e8bafff",
                            transform: "translateY(-3px)",
                            boxShadow: "0 8px 20px rgba(30,139,175,0.15)",
                          },
                          minHeight: 70,
                        }}
                      >
                        {renderContent(option.answer)}
                      </Button>
                    ))}
                  </Box> */}

                  {/* Show Answer / Revealed + Feedback */}
                  {!showAnswer ? (
                    <Box sx={{ mt: "auto", display: "flex", justifyContent: "center" }}>
                      <Button
                        onClick={() => setShowAnswer(true)}
                        sx={{
                          background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                          color: "#fff",
                          padding: "10px 32px",
                          borderRadius: 30,
                          fontSize: "0.95rem",
                          fontWeight: "bold",
                          textTransform: "none",
                          boxShadow: "0 5px 14px rgba(102,126,234,0.3)",
                          transition: "all 0.3s",
                          "&:hover": {
                            background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)",
                            transform: "translateY(-2px)",
                            boxShadow: "0 8px 20px rgba(102,126,234,0.45)",
                          },
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <span style={{ fontSize: "1.2rem" }}>👁️</span>
                        Show Answer
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 4, pt: 3, borderTop: "2px solid #e0e0e0" }}>
                      {/* Correct Answer */}
                    <Box
            sx={{
              background: "rgba(76,175,80,0.12)",
              border: "2px solid #4caf50",
              borderRadius: 12,
              p: 4,
              mb: 4,
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "#2e7d32", fontWeight: 700, mb: 3, alignSelf: "center", justifySelf: "center" }}
            >
              ✓ Correct Answer
            </Typography>

            {/* Mapped Correct Answers with their Explanations */}
            {viewedCard?.answer
              ?.filter((ans) => ans.is_correct)
              .map((correctAns, idx) => (
                <Box key={idx} sx={{ mb: 4, textAlign: "center" }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 2,
                    }}
                  >
                    {renderContent(correctAns.answer)}
                  </Typography>

                  {correctAns.explanation && (
                    <Box sx={{ mt: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ 
                          fontWeight: 600, 
                          color: "#1e8bafff",
                          textAlign: "center"
                        }}
                      >
                        Explanation
                      </Typography>
                      <Box
                        sx={{
                          fontSize: "1.05rem",
                          lineHeight: 1.7,
                          color: "#333",
                          textAlign: "center",
                          padding: "16px",
                          borderRadius: "10px",
                        }}
                      >
                        {renderContent(correctAns.explanation)}
                      </Box>
                    </Box>
                  )}
                </Box>
              )) || (
              <Typography variant="body1" color="text.secondary">
                No correct answer set
              </Typography>
            )}
          </Box>

                      {/* Feedback - exact structure from your code */}
                      <Typography
                        variant="subtitle2"
                        align="center"
                        sx={{ mb: 2, color: "#666", fontWeight: 600, mt: 2 }}
                      >
                        How well did you know this?
                      </Typography>

                      <Box sx={{ display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 1 }}>
                        <ImageButton
                          onClick={() => { /* intentionally empty - no action */ }}
                          sx={{ color: "#555" }}
                        >
                          <Image src={skip} alt="skip" />
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            Skip
                          </Typography>
                        </ImageButton>

                        <ImageButton
                          onClick={() => { /* intentionally empty */ }}
                          sx={{ color: "#555" }}
                        >
                          <Image src={forgot} alt="forgot" />
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            Forgot
                          </Typography>
                        </ImageButton>

                        <ImageButton
                          onClick={() => { /* intentionally empty */ }}
                          sx={{ color: "#555" }}
                        >
                          <Image src={partially} alt="partially" />
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            Partial
                          </Typography>
                        </ImageButton>

                        <ImageButton
                          onClick={() => { /* intentionally empty */ }}
                          sx={{ color: "#555" }}
                        >
                          <Image src={efforts} alt="efforts" />
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            With Effort
                          </Typography>
                        </ImageButton>

                        <ImageButton
                          onClick={() => { /* intentionally empty */ }}
                          sx={{ color: "#555" }}
                        >
                          <Image src={convenient} alt="convenient" />
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            Easy
                          </Typography>
                        </ImageButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Dialog>

          </div>
        </>
      )}
    </div>
  );
}

export default FillInTheBlanks;              



















































